// ---------------------------------
// client/src/pages/AdminPdfTemplates.jsx
// Admin list + upload for PDF templates
// ---------------------------------
import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    createPdfTemplate,
    deletePdfTemplate,
    fetchPdfTemplates,
    updatePdfTemplate,
    bulkUpdatePdfTemplateFields,
    checkPdfTemplateFieldBulkMatch,
    downloadPdfTemplateFile,
    copyTemplatePrefill,
    deleteTemplatePrefill,
    updateTemplatePrefill,
} from '../utils/pdfTemplates';
import { fetchAllJobTypes } from '../utils/jobs';
import { formatDate } from '../utils/date';
import PdfThumbnail from '../components/PdfThumbnail';
import '../css/adminPdfTemplates.css';

const TEXT = {
    title: 'תבניות PDF',
    description: 'ניהול תבניות טפסים המבוססות על PDF.',
    upload: 'העלאת תבנית',
    uploading: 'מעלה...',
    name: 'שם התבנית',
    pdf: 'קובץ PDF / DOCX / DOC',
    jobTypes: 'סוגי עבודה משויכים',
    jobTypesHint: 'בחרו סוגי עבודה שיוכלו להשתמש בתבנית.',
    edit: 'עריכה',
    update: 'עדכון',
    updating: 'מעדכן...',
    download: 'הורדת PDF',
    editTitle: 'עריכת תבנית',
    replacePdf: 'החלפת PDF / DOCX / DOC (לא חובה)',
    search: 'חיפוש',
    searchPlaceholder: 'חפשו לפי שם תבנית',
    filterJobTypes: 'סינון לפי סוגי עבודה',
    noMatches: 'לא נמצאו תבניות מתאימות.',
    cancel: 'ביטול',
    save: 'שמירה',
    openBuilder: 'פתיחת בונה',
    delete: 'מחיקה',
    empty: 'אין תבניות עדיין. העלו PDF ראשון.',
    loading: 'טוען תבניות...',
    deleteConfirm: 'האם למחוק את התבנית? פעולה זו אינה הפיכה.',
    loadError: 'לא ניתן לטעון תבניות.',
    uploadError: 'לא ניתן להעלות תבנית.',
    deleteError: 'לא ניתן למחוק תבנית.',
    jobTypesEmpty: 'אין סוגי עבודה זמינים.',
    bulkEdit: 'עדכון שדות גלובלי',
    bulkEditTitle: 'עדכון שדות גלובלי',
    bulkFieldName: 'ערך חיפוש',
    bulkSearchField: 'חפש לפי',
    bulkSearchLabel: 'תווית שדה',
    bulkSearchId: 'מזהה שדה',
    bulkMatchType: 'סוג התאמה',
    bulkFieldType: 'סוג שדה',
    bulkCheck: 'בדוק שדות',
    bulkChecking: 'בודק...',
    bulkCheckResult: 'נמצאו שדות תואמים',
    bulkUpdateRequired: 'עדכון חובה',
    bulkRequiredValue: 'ערך חובה',
    bulkUpdateAutoFill: 'עדכון מילוי אוטומטי',
    bulkAutoFillValue: 'ערך מילוי אוטומטי',
    bulkUpdateSuggested: 'עדכון ערכים מוצעים',
    bulkSuggestedValues: 'ערכים מוצעים',
    bulkUpdatePlaceholder: 'עדכון מציין מקום',
    bulkPlaceholder: 'מציין מקום',
    bulkSave: 'החל עדכון',
    bulkSaving: 'מעדכן...',
    bulkSuccess: 'השדות עודכנו.',
    bulkError: 'לא ניתן לעדכן שדות.',
    showThumbnails: 'הצג תמונות ממוזערות',
    prefillOptions: 'מילויים מראש',
    prefillEmpty: 'אין מילויים מראש.',
    prefillDeleteConfirm: 'למחוק את המילוי מראש? הפעולה אינה הפיכה.',
    prefillDeleteError: 'לא ניתן למחוק מילוי מראש.',
    prefillRename: 'עריכת שם מילוי מראש',
    prefillRenameSave: 'שמור',
    prefillRenameCancel: 'ביטול',
    prefillRenameError: 'לא ניתן לעדכן שם מילוי מראש.',
    prefillRenamePlaceholder: 'שם המילוי מראש',
    prefillCopy: 'העתק מילוי',
    prefillCopyTitle: 'העתקת מילוי מראש',
    prefillCopySourceTemplate: 'תבנית מקור',
    prefillCopySourcePrefill: 'מילוי מקור',
    prefillCopyName: 'שם למילוי החדש',
    prefillCopySubmit: 'העתק',
    prefillCopySaving: 'מעתיק...',
    prefillCopyEmpty: 'אין מילויים מראש בתבניות אחרות.',
    prefillCopySuccess: 'המילוי הועתק.',
    prefillCopyError: 'לא ניתן להעתיק מילוי מראש.',
    emailShareEnabled: 'שיתוף בדוא"ל',
    emailShareToggleError: 'לא ניתן לעדכן שיתוף בדוא"ל.',
};

const MATCH_OPTIONS = [
    { value: 'contains', label: 'מכיל' },
    { value: 'startsWith', label: 'מתחיל עם' },
    { value: 'equals', label: 'התאמה מלאה' },
];

const FIELD_TYPE_OPTIONS = [
    { value: '', label: 'כל הסוגים' },
    { value: 'text', label: 'טקסט' },
    { value: 'number', label: 'מספר' },
    { value: 'dropdown', label: 'רשימה נפתחת' },
    { value: 'checkbox', label: 'תיבת סימון' },
    { value: 'date', label: 'תאריך' },
    { value: 'time', label: 'שעה' },
    { value: 'signature', label: 'חתימה' },
];

const AUTO_FILL_OPTIONS = [
    { value: '', label: 'ללא' },
    { value: 'job_number', label: 'מספר עבודה' },
    { value: 'worker_name', label: 'שם עובד' },
    { value: 'worker_role', label: 'תפקיד עובד' },
    { value: 'recipient_company', label: 'שם החברה המקבלת שירות' },
    { value: 'date_today', label: 'תאריך היום' },
    { value: 'date_form_start', label: 'תאריך תחילת הטופס' },
    { value: 'date_on_click', label: 'תאריך בלחיצה' },
    { value: 'time_now', label: 'שעה נוכחית' },
    { value: 'time_form_start', label: 'שעת תחילת הטופס' },
    { value: 'time_on_click', label: 'שעה בלחיצה' },
];

export default function AdminPdfTemplates() {
    const navigate = useNavigate();
    const thumbnailPrefKey = 'adminPdfTemplates_showThumbnails';
    const [templates, setTemplates] = useState([]);
    const [jobTypes, setJobTypes] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState('');
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [editingTemplate, setEditingTemplate] = useState(null);
    const [formState, setFormState] = useState({
        name: '',
        file: null,
        jobTypeIds: [],
    });
    const [isSaving, setIsSaving] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const [editingPrefillId, setEditingPrefillId] = useState('');
    const [editingPrefillName, setEditingPrefillName] = useState('');
    const [editingPrefillTemplateId, setEditingPrefillTemplateId] = useState('');
    const [prefillUpdating, setPrefillUpdating] = useState(() => new Set());
    const [isCopyPrefillOpen, setIsCopyPrefillOpen] = useState(false);
    const [copyPrefillTarget, setCopyPrefillTarget] = useState(null);
    const [copyPrefillState, setCopyPrefillState] = useState({
        sourceTemplateId: '',
        sourcePrefillId: '',
        name: '',
    });
    const [isCopyPrefillSaving, setIsCopyPrefillSaving] = useState(false);
    const [copyPrefillError, setCopyPrefillError] = useState('');
    const [copyPrefillFeedback, setCopyPrefillFeedback] = useState('');
    const [shareToggleUpdating, setShareToggleUpdating] = useState(() => new Set());
    const [selectedJobTypeIds, setSelectedJobTypeIds] = useState(() => new Set());
    const [showThumbnails, setShowThumbnails] = useState(() => {
        if (typeof window === 'undefined') return false;
        return window.localStorage.getItem(thumbnailPrefKey) === 'true';
    });
    const [isBulkEditOpen, setIsBulkEditOpen] = useState(false);
    const [isBulkSaving, setIsBulkSaving] = useState(false);
    const [isBulkChecking, setIsBulkChecking] = useState(false);
    const [bulkEditError, setBulkEditError] = useState('');
    const [bulkEditFeedback, setBulkEditFeedback] = useState('');
    const [bulkMatchCount, setBulkMatchCount] = useState(null);
    const [bulkEditState, setBulkEditState] = useState({
        fieldName: '',
        searchField: 'label',
        matchType: 'contains',
        fieldType: '',
        updateRequired: false,
        requiredValue: false,
        updateAutoFill: false,
        autoFill: '',
        updateSuggestedValues: false,
        suggestedValues: '',
        updatePlaceholder: false,
        placeholder: '',
    });

    useEffect(() => {
        const load = async () => {
            try {
                setIsLoading(true);
                const [templateData, jobTypeData] = await Promise.all([
                    fetchPdfTemplates(),
                    fetchAllJobTypes(),
                ]);
                setTemplates(templateData);
                setJobTypes(jobTypeData);
            } catch (err) {
                setError(err.response?.data?.message || TEXT.loadError);
            } finally {
                setIsLoading(false);
            }
        };
        load();
    }, []);

    useEffect(() => {
        if (typeof window === 'undefined') return;
        window.localStorage.setItem(thumbnailPrefKey, showThumbnails ? 'true' : 'false');
    }, [showThumbnails]);

    useEffect(() => {
        setBulkMatchCount(null);
    }, [
        bulkEditState.fieldName,
        bulkEditState.searchField,
        bulkEditState.matchType,
        bulkEditState.fieldType,
    ]);

    const jobTypeOptions = useMemo(() => {
        return [...jobTypes].sort((a, b) => a.name.localeCompare(b.name, 'he'));
    }, [jobTypes]);

    const copyPrefillSourceTemplates = useMemo(
        () =>
            templates.filter(
                (template) =>
                    template.id !== copyPrefillTarget?.id &&
                    Array.isArray(template.prefillOptions) &&
                    template.prefillOptions.length > 0,
            ),
        [copyPrefillTarget?.id, templates],
    );

    const selectedCopySourceTemplate = useMemo(
        () =>
            copyPrefillSourceTemplates.find(
                (template) => String(template.id) === String(copyPrefillState.sourceTemplateId),
            ) || null,
        [copyPrefillSourceTemplates, copyPrefillState.sourceTemplateId],
    );

    const selectedCopySourcePrefill = useMemo(
        () =>
            (selectedCopySourceTemplate?.prefillOptions || []).find(
                (prefill) => String(prefill.id) === String(copyPrefillState.sourcePrefillId),
            ) || null,
        [selectedCopySourceTemplate, copyPrefillState.sourcePrefillId],
    );


    const filteredTemplates = useMemo(() => {
        const term = searchTerm.trim().toLocaleLowerCase('he-IL');
        const hasJobTypeFilter = selectedJobTypeIds.size > 0;
        return templates.filter((template) => {
            if (term) {
                const nameMatch = (template.name || '').toLocaleLowerCase('he-IL').includes(term);
                if (!nameMatch) return false;
            }
            if (hasJobTypeFilter) {
                const templateJobTypes = template.jobTypes || [];
                const hasMatch = templateJobTypes.some((jobType) =>
                    selectedJobTypeIds.has(String(jobType.id)),
                );
                if (!hasMatch) return false;
            }
            return true;
        });
    }, [templates, searchTerm, selectedJobTypeIds]);

    const toggleJobTypeFilter = (jobTypeId) => {
        setSelectedJobTypeIds((prev) => {
            const next = new Set(prev);
            if (next.has(jobTypeId)) {
                next.delete(jobTypeId);
            } else {
                next.add(jobTypeId);
            }
            return next;
        });
    };

    const resetForm = () => {
        setFormState({
            name: '',
            file: null,
            jobTypeIds: [],
        });
        setEditingTemplate(null);
        setIsModalOpen(false);
        setIsSaving(false);
    };

    const openCreateModal = () => {
        setEditingTemplate(null);
        setFormState({
            name: '',
            file: null,
            jobTypeIds: [],
        });
        setIsModalOpen(true);
    };

    const openEditModal = (template) => {
        setEditingTemplate(template);
        setFormState({
            name: template?.name || '',
            file: null,
            jobTypeIds: [],
        });
        setIsModalOpen(true);
    };

    const toggleJobType = (jobTypeId) => {
        setFormState((prev) => {
            const next = new Set(prev.jobTypeIds);
            if (next.has(jobTypeId)) {
                next.delete(jobTypeId);
            } else {
                next.add(jobTypeId);
            }
            return { ...prev, jobTypeIds: Array.from(next) };
        });
    };

    const openBulkEditModal = () => {
        setBulkEditError('');
        setBulkEditFeedback('');
        setBulkMatchCount(null);
        setIsBulkEditOpen(true);
    };

    const closeBulkEditModal = () => {
        if (isBulkSaving || isBulkChecking) return;
        setIsBulkEditOpen(false);
    };

    const handleBulkEditSubmit = async (event) => {
        event.preventDefault();
        setBulkEditError('');
        setBulkEditFeedback('');
        if (!bulkEditState.fieldName.trim()) {
            setBulkEditError(TEXT.bulkError);
            return;
        }
        const updates = {};
        if (bulkEditState.updateRequired) {
            updates.required = bulkEditState.requiredValue;
        }
        if (bulkEditState.updateAutoFill) {
            updates.autoFill = bulkEditState.autoFill;
        }
        if (bulkEditState.updateSuggestedValues) {
            updates.suggestedValues = bulkEditState.suggestedValues;
        }
        if (bulkEditState.updatePlaceholder) {
            updates.placeholder = bulkEditState.placeholder;
        }
        if (Object.keys(updates).length === 0) {
            setBulkEditError(TEXT.bulkError);
            return;
        }
        try {
            setIsBulkSaving(true);
            const response = await bulkUpdatePdfTemplateFields({
                fieldName: bulkEditState.fieldName,
                searchField: bulkEditState.searchField,
                matchType: bulkEditState.matchType,
                fieldType: bulkEditState.fieldType || undefined,
                updates,
            });
            setBulkEditFeedback(`${TEXT.bulkSuccess} (${response.updated || 0})`);
        } catch (err) {
            setBulkEditError(err.response?.data?.message || TEXT.bulkError);
        } finally {
            setIsBulkSaving(false);
        }
    };

    const handleBulkEditCheck = async () => {
        setBulkEditError('');
        setBulkEditFeedback('');
        if (!bulkEditState.fieldName.trim()) {
            setBulkEditError(TEXT.bulkError);
            return;
        }
        try {
            setIsBulkChecking(true);
            const response = await checkPdfTemplateFieldBulkMatch({
                fieldName: bulkEditState.fieldName,
                searchField: bulkEditState.searchField,
                matchType: bulkEditState.matchType,
                fieldType: bulkEditState.fieldType || undefined,
            });
            setBulkMatchCount(Number(response?.matched) || 0);
        } catch (err) {
            setBulkEditError(err.response?.data?.message || TEXT.bulkError);
        } finally {
            setIsBulkChecking(false);
        }
    };

    const handleSubmit = async (event) => {
        event.preventDefault();
        if (!formState.name.trim()) {
            return;
        }
        if (!editingTemplate && !formState.file) {
            return;
        }
        try {
            setIsSaving(true);
            if (editingTemplate) {
                const updated = await updatePdfTemplate(editingTemplate.id, {
                    name: formState.name.trim(),
                    file: formState.file || null,
                });
                setTemplates((prev) => prev.map((item) => (item.id === updated.id ? updated : item)));
            } else {
                const created = await createPdfTemplate({
                    name: formState.name.trim(),
                    file: formState.file,
                    jobTypeIds: formState.jobTypeIds,
                });
                setTemplates((prev) => [created, ...prev]);
            }
            resetForm();
        } catch (err) {
            setError(err.response?.data?.message || TEXT.uploadError);
            setIsSaving(false);
        }
    };

    const handleDelete = async (templateId) => {
        if (!window.confirm(TEXT.deleteConfirm)) {
            return;
        }
        try {
            await deletePdfTemplate(templateId);
            setTemplates((prev) => prev.filter((template) => template.id !== templateId));
        } catch (err) {
            setError(err.response?.data?.message || TEXT.deleteError);
        }
    };

    const handleDeletePrefill = async (templateId, prefillId) => {
        if (!window.confirm(TEXT.prefillDeleteConfirm)) {
            return;
        }
        try {
            await deleteTemplatePrefill(templateId, prefillId);
            setTemplates((prev) =>
                prev.map((template) => {
                    if (template.id !== templateId) return template;
                    const nextPrefills = (template.prefillOptions || []).filter(
                        (prefill) => prefill.id !== prefillId,
                    );
                    return { ...template, prefillOptions: nextPrefills };
                }),
            );
        } catch (err) {
            setError(err.response?.data?.message || TEXT.prefillDeleteError);
        }
    };

    const handleDownload = async (template) => {
        try {
            const safeName = `${template?.name || 'template'}.pdf`;
            await downloadPdfTemplateFile(template.id, safeName);
        } catch (err) {
            setError(err.response?.data?.message || TEXT.loadError);
        }
    };

    const startEditPrefill = (templateId, prefill) => {
        setEditingPrefillId(prefill.id);
        setEditingPrefillTemplateId(templateId);
        setEditingPrefillName(prefill.name || '');
        setError('');
    };

    const cancelEditPrefill = () => {
        setEditingPrefillId('');
        setEditingPrefillTemplateId('');
        setEditingPrefillName('');
    };

    const handleSavePrefillName = async () => {
        if (!editingPrefillId || !editingPrefillTemplateId) {
            return;
        }
        const nextName = editingPrefillName.trim();
        if (!nextName) {
            setError(TEXT.prefillRenameError);
            return;
        }
        setPrefillUpdating((prev) => new Set(prev).add(editingPrefillId));
        setError('');
        try {
            const updated = await updateTemplatePrefill(
                editingPrefillTemplateId,
                editingPrefillId,
                { name: nextName },
            );
            setTemplates((prev) =>
                prev.map((template) => {
                    if (template.id !== editingPrefillTemplateId) {
                        return template;
                    }
                    const nextPrefills = (template.prefillOptions || []).map((prefill) =>
                        prefill.id === editingPrefillId
                            ? { ...prefill, name: updated?.name || nextName, updatedAt: updated?.updatedAt || prefill.updatedAt }
                            : prefill,
                    );
                    return { ...template, prefillOptions: nextPrefills };
                }),
            );
            cancelEditPrefill();
        } catch (err) {
            setError(err.response?.data?.message || TEXT.prefillRenameError);
        } finally {
            setPrefillUpdating((prev) => {
                const next = new Set(prev);
                next.delete(editingPrefillId);
                return next;
            });
        }
    };

    const openCopyPrefillModal = (template) => {
        const sources = templates.filter(
            (item) =>
                item.id !== template.id &&
                Array.isArray(item.prefillOptions) &&
                item.prefillOptions.length > 0,
        );
        const firstSource = sources[0] || null;
        const firstPrefill = firstSource?.prefillOptions?.[0] || null;
        setCopyPrefillTarget(template);
        setCopyPrefillState({
            sourceTemplateId: firstSource?.id || '',
            sourcePrefillId: firstPrefill?.id || '',
            name: firstPrefill?.name || '',
        });
        setCopyPrefillError('');
        setCopyPrefillFeedback('');
        setIsCopyPrefillOpen(true);
    };

    const closeCopyPrefillModal = () => {
        if (isCopyPrefillSaving) return;
        setIsCopyPrefillOpen(false);
        setCopyPrefillTarget(null);
        setCopyPrefillError('');
        setCopyPrefillFeedback('');
    };

    const handleCopySourceTemplateChange = (templateId) => {
        const source = copyPrefillSourceTemplates.find((template) => String(template.id) === String(templateId));
        const firstPrefill = source?.prefillOptions?.[0] || null;
        setCopyPrefillState({
            sourceTemplateId: templateId,
            sourcePrefillId: firstPrefill?.id || '',
            name: firstPrefill?.name || '',
        });
    };

    const handleCopySourcePrefillChange = (prefillId) => {
        const prefill = (selectedCopySourceTemplate?.prefillOptions || []).find(
            (item) => String(item.id) === String(prefillId),
        );
        setCopyPrefillState((prev) => ({
            ...prev,
            sourcePrefillId: prefillId,
            name: prefill?.name || prev.name,
        }));
    };

    const handleCopyPrefillSubmit = async (event) => {
        event.preventDefault();
        if (!copyPrefillTarget?.id || !copyPrefillState.sourceTemplateId || !copyPrefillState.sourcePrefillId) {
            setCopyPrefillError(TEXT.prefillCopyError);
            return;
        }
        setIsCopyPrefillSaving(true);
        setCopyPrefillError('');
        setCopyPrefillFeedback('');
        try {
            const copied = await copyTemplatePrefill(copyPrefillTarget.id, {
                sourceTemplateId: copyPrefillState.sourceTemplateId,
                sourcePrefillId: copyPrefillState.sourcePrefillId,
                name: copyPrefillState.name.trim() || selectedCopySourcePrefill?.name || '',
            });
            setTemplates((prev) =>
                prev.map((template) => {
                    if (template.id !== copyPrefillTarget.id) return template;
                    const nextPrefills = [
                        ...(template.prefillOptions || []),
                        {
                            id: copied.id,
                            name: copied.name,
                            updatedAt: copied.updatedAt,
                        },
                    ];
                    return { ...template, prefillOptions: nextPrefills };
                }),
            );
            setCopyPrefillFeedback(
                `${TEXT.prefillCopySuccess} (${copied.copiedCount || 0}/${(copied.copiedCount || 0) + (copied.skippedCount || 0)})`,
            );
            setIsCopyPrefillOpen(false);
            setCopyPrefillTarget(null);
        } catch (err) {
            setCopyPrefillError(err.response?.data?.message || TEXT.prefillCopyError);
        } finally {
            setIsCopyPrefillSaving(false);
        }
    };

    const handleToggleTemplateShare = async (template) => {
        const nextValue = !template.isShareableByEmail;
        setShareToggleUpdating((prev) => new Set(prev).add(template.id));
        setError('');
        try {
            const updated = await updatePdfTemplate(template.id, {
                isShareableByEmail: nextValue,
            });
            setTemplates((prev) =>
                prev.map((item) =>
                    item.id === template.id
                        ? { ...item, isShareableByEmail: Boolean(updated?.isShareableByEmail ?? nextValue) }
                        : item,
                ),
            );
        } catch (err) {
            setError(err.response?.data?.message || TEXT.emailShareToggleError);
        } finally {
            setShareToggleUpdating((prev) => {
                const next = new Set(prev);
                next.delete(template.id);
                return next;
            });
        }
    };

    return (
        <div className="page page--admin-templates page--accent stack stack--lg">
            <header className="page__header">
                <div className="stack">
                    <h1 className="page__title">{TEXT.title}</h1>
                    <p className="page__description">{TEXT.description}</p>
                </div>
                <div className="flex flex--wrap">
                    <button type="button" className="button button--primary" onClick={openCreateModal}>
                        {TEXT.upload}
                    </button>
                    <button type="button" className="button button--light" onClick={openBulkEditModal}>
                        {TEXT.bulkEdit}
                    </button>
                </div>
            </header>

            {error && <div className="alert alert--danger">{error}</div>}

            {!isLoading && templates.length > 0 && (
                <section className="card template-filters">
                    <label className="form-field">
                        <span className="form-field__label">{TEXT.search}</span>
                        <input
                            className="form-field__control"
                            type="search"
                            placeholder={TEXT.searchPlaceholder}
                            value={searchTerm}
                            onChange={(event) => setSearchTerm(event.target.value)}
                        />
                    </label>
                    <label className="check-toggle">
                        <input
                            type="checkbox"
                            checked={showThumbnails}
                            onChange={(event) => setShowThumbnails(event.target.checked)}
                        />
                        <span>{TEXT.showThumbnails}</span>
                    </label>
                    <div className="stack">
                        <span className="form-field__label">{TEXT.filterJobTypes}</span>
                        <div className="filter-chips">
                            {jobTypeOptions.map((jobType) => {
                                const id = String(jobType.id);
                                const isActive = selectedJobTypeIds.has(id);
                                return (
                                    <label
                                        key={jobType.id}
                                        className={`chip chip--filter${isActive ? ' chip--filter--active' : ''}`}
                                    >
                                        <input
                                            type="checkbox"
                                            checked={isActive}
                                            onChange={() => toggleJobTypeFilter(id)}
                                        />
                                        <span>{jobType.name}</span>
                                    </label>
                                );
                            })}
                            {jobTypeOptions.length === 0 && <span className="card__meta">{TEXT.jobTypesEmpty}</span>}
                        </div>
                    </div>
                </section>
            )}

            {isLoading && <div className="card">{TEXT.loading}</div>}
            {!isLoading && templates.length === 0 && <div className="card">{TEXT.empty}</div>}

            {!isLoading && templates.length > 0 && filteredTemplates.length === 0 && (
                <div className="card">{TEXT.noMatches}</div>
            )}

            {!isLoading && filteredTemplates.length > 0 && (
                <div className="card-grid card-grid--two">
                    {filteredTemplates.map((template) => (
                        <article key={template.id} className="card template-card">
                            <div className={`template-card__main${showThumbnails ? '' : ' template-card__main--full'}`}>
                                <div className="template-card__info">
                                    <h3 className="card__title">{template.name}</h3>
                                    <div className="template-card__meta">
                                        <span className="card__meta">{template.pageCount} {'עמודים'}</span>
                                        <span className="card__meta">{'עודכן'} {formatDate(template.updatedAt)}</span>
                                    </div>
                                    {template.jobTypes && template.jobTypes.length > 0 ? (
                                        <div className="inline-pills template-card__chips">
                                            {template.jobTypes.map((jobType) => (
                                                <span key={jobType.id} className="chip">
                                                    {jobType.name}
                                                </span>
                                            ))}
                                        </div>
                                    ) : (
                                        <span className="card__meta">{TEXT.jobTypesEmpty}</span>
                                    )}
                                    {template.prefillOptions && template.prefillOptions.length > 0 ? (
                                        <div className="template-prefills">
                                            <span className="card__meta">{TEXT.prefillOptions}</span>
                                            <div className="prefill-list">
                                                {template.prefillOptions.map((prefill) => {
                                                    const isEditing =
                                                        editingPrefillId === prefill.id &&
                                                        editingPrefillTemplateId === template.id;
                                                    const isUpdating = prefillUpdating.has(prefill.id);
                                                    return (
                                                        <div
                                                            key={prefill.id}
                                                            className={`prefill-pill${isEditing ? ' prefill-pill--editing' : ''}`}
                                                        >
                                                            {isEditing ? (
                                                                <>
                                                                    <input
                                                                        className="prefill-pill__input"
                                                                        type="text"
                                                                        value={editingPrefillName}
                                                                        placeholder={TEXT.prefillRenamePlaceholder}
                                                                        onChange={(event) => setEditingPrefillName(event.target.value)}
                                                                        onKeyDown={(event) => {
                                                                            if (event.key === 'Enter') {
                                                                                event.preventDefault();
                                                                                handleSavePrefillName();
                                                                            }
                                                                            if (event.key === 'Escape') {
                                                                                event.preventDefault();
                                                                                cancelEditPrefill();
                                                                            }
                                                                        }}
                                                                        disabled={isUpdating}
                                                                    />
                                                                    <button
                                                                        type="button"
                                                                        className="icon-button icon-button--compact"
                                                                        onClick={handleSavePrefillName}
                                                                        disabled={isUpdating}
                                                                        title={TEXT.prefillRenameSave}
                                                                        aria-label={TEXT.prefillRenameSave}
                                                                    >
                                                                        {'✓'}
                                                                    </button>
                                                                    <button
                                                                        type="button"
                                                                        className="icon-button icon-button--compact"
                                                                        onClick={cancelEditPrefill}
                                                                        disabled={isUpdating}
                                                                        title={TEXT.prefillRenameCancel}
                                                                        aria-label={TEXT.prefillRenameCancel}
                                                                    >
                                                                        {'×'}
                                                                    </button>
                                                                </>
                                                            ) : (
                                                                <>
                                                                    <button
                                                                        type="button"
                                                                        className="prefill-pill__name"
                                                                        onClick={() => startEditPrefill(template.id, prefill)}
                                                                        title={TEXT.prefillRename}
                                                                    >
                                                                        {prefill.name}
                                                                    </button>
                                                                    <button
                                                                        type="button"
                                                                        className="icon-button icon-button--compact"
                                                                        onClick={() => handleDeletePrefill(template.id, prefill.id)}
                                                                        disabled={isUpdating}
                                                                    >
                                                                        {'×'}
                                                                    </button>
                                                                </>
                                                            )}
                                                        </div>
                                                    );
                                                })}
                                            </div>
                                        </div>
                                    ) : (
                                        <span className="card__meta">{TEXT.prefillEmpty}</span>
                                    )}
                                </div>
                                {showThumbnails && (
                                    <PdfThumbnail
                                        fileUrl={template.fileUrl}
                                        label={template.name}
                                        width={160}
                                        className="template-card__thumb"
                                    />
                                )}
                            </div>
                            <div className="template-card__features" aria-label={TEXT.prefillOptions}>
                                <button
                                    type="button"
                                    className="button button--primary template-card__feature-button"
                                    onClick={() => openCopyPrefillModal(template)}
                                >
                                    {TEXT.prefillCopyTitle}
                                </button>
                                <label className="check-toggle template-share-toggle">
                                    <input
                                        type="checkbox"
                                        checked={Boolean(template.isShareableByEmail)}
                                        onChange={() => handleToggleTemplateShare(template)}
                                        disabled={shareToggleUpdating.has(template.id)}
                                    />
                                    <span>{TEXT.emailShareEnabled}</span>
                                </label>
                            </div>
                            <div className="flex flex--wrap template-card__actions">
                                <button
                                    type="button"
                                    className="button button--primary"
                                    onClick={() => navigate(`/admin/pdf-templates/${template.id}/builder`)}
                                >
                                    {TEXT.openBuilder}
                                </button>
                                <button
                                    type="button"
                                    className="button button--light"
                                    onClick={() => openEditModal(template)}
                                >
                                    {TEXT.edit}
                                </button>
                                {template.fileUrl && (
                                    <button
                                        type="button"
                                        className="button button--ghost"
                                        onClick={() => handleDownload(template)}
                                    >
                                        {TEXT.download}
                                    </button>
                                )}
                                <button
                                    type="button"
                                    className="button button--danger"
                                    onClick={() => handleDelete(template.id)}
                                >
                                    {TEXT.delete}
                                </button>
                            </div>
                        </article>
                    ))}
                </div>
            )}

            {isModalOpen && (
                <div className="modal-overlay" role="dialog" aria-modal="true">
                    <div className="modal-content">
                        <form className="modal-form" onSubmit={handleSubmit}>
                            <header className="stack">
                                <h2 className="card__title">
                                    {editingTemplate ? TEXT.editTitle : TEXT.upload}
                                </h2>
                            </header>
                            <div className="modal-form__body">
                                <label className="form-field">
                                    <span className="form-field__label">{TEXT.name}</span>
                                    <input
                                        className="form-field__control"
                                        type="text"
                                        value={formState.name}
                                        onChange={(event) =>
                                            setFormState((prev) => ({ ...prev, name: event.target.value }))
                                        }
                                        required
                                    />
                                </label>
                                <label className="form-field">
                                    <span className="form-field__label">
                                        {editingTemplate ? TEXT.replacePdf : TEXT.pdf}
                                    </span>
                                    <input
                                        className="form-field__control"
                                        type="file"
                                        accept="application/pdf,application/vnd.openxmlformats-officedocument.wordprocessingml.document,application/msword,.pdf,.docx,.doc"
                                        onChange={(event) =>
                                            setFormState((prev) => ({ ...prev, file: event.target.files?.[0] || null }))
                                        }
                                        required={!editingTemplate}
                                    />
                                </label>
                                {!editingTemplate && (
                                    <div className="stack">
                                        <span className="form-field__label">{TEXT.jobTypes}</span>
                                        <span className="form-field__description">{TEXT.jobTypesHint}</span>
                                        <div className="filter-chips">
                                            {jobTypeOptions.map((jobType) => {
                                                const id = String(jobType.id);
                                                const isActive = formState.jobTypeIds.includes(id);
                                                return (
                                                    <label
                                                        key={jobType.id}
                                                        className={`chip chip--filter${isActive ? ' chip--filter--active' : ''}`}
                                                    >
                                                        <input
                                                            type="checkbox"
                                                            checked={isActive}
                                                            onChange={() => toggleJobType(id)}
                                                        />
                                                        <span>{jobType.name}</span>
                                                    </label>
                                                );
                                            })}
                                            {jobTypeOptions.length === 0 && <span className="card__meta">{TEXT.jobTypesEmpty}</span>}
                                        </div>
                                    </div>
                                )}
                            </div>
                            <footer className="modal-form__footer">
                                <button type="button" className="button button--ghost" onClick={resetForm}>
                                    {TEXT.cancel}
                                </button>
                                <button type="submit" className="button button--primary" disabled={isSaving}>
                                    {editingTemplate
                                        ? isSaving ? TEXT.updating : TEXT.update
                                        : isSaving ? TEXT.uploading : TEXT.save}
                                </button>
                            </footer>
                        </form>
                    </div>
                </div>
            )}

            {isCopyPrefillOpen && (
                <div
                    className="modal-overlay"
                    role="dialog"
                    aria-modal="true"
                    onClick={(event) => {
                        if (event.target === event.currentTarget) {
                            closeCopyPrefillModal();
                        }
                    }}
                >
                    <div className="modal-content" role="document">
                        <header className="stack">
                            <h2 className="card__title">{TEXT.prefillCopyTitle}</h2>
                            {copyPrefillTarget && <p className="card__meta">{copyPrefillTarget.name}</p>}
                        </header>
                        {copyPrefillSourceTemplates.length === 0 ? (
                            <div className="modal-form">
                                <div className="modal-form__body">
                                    <div className="modal-form__empty">{TEXT.prefillCopyEmpty}</div>
                                </div>
                                <footer className="modal-form__footer">
                                    <button type="button" className="button button--ghost" onClick={closeCopyPrefillModal}>
                                        {TEXT.cancel}
                                    </button>
                                </footer>
                            </div>
                        ) : (
                            <form className="modal-form" onSubmit={handleCopyPrefillSubmit}>
                                <div className="modal-form__body">
                                    <label className="form-field">
                                        <span className="form-field__label">{TEXT.prefillCopySourceTemplate}</span>
                                        <select
                                            className="form-field__control"
                                            value={copyPrefillState.sourceTemplateId}
                                            onChange={(event) => handleCopySourceTemplateChange(event.target.value)}
                                            disabled={isCopyPrefillSaving}
                                        >
                                            {copyPrefillSourceTemplates.map((template) => (
                                                <option key={template.id} value={template.id}>
                                                    {template.name}
                                                </option>
                                            ))}
                                        </select>
                                    </label>
                                    <label className="form-field">
                                        <span className="form-field__label">{TEXT.prefillCopySourcePrefill}</span>
                                        <select
                                            className="form-field__control"
                                            value={copyPrefillState.sourcePrefillId}
                                            onChange={(event) => handleCopySourcePrefillChange(event.target.value)}
                                            disabled={isCopyPrefillSaving}
                                        >
                                            {(selectedCopySourceTemplate?.prefillOptions || []).map((prefill) => (
                                                <option key={prefill.id} value={prefill.id}>
                                                    {prefill.name}
                                                </option>
                                            ))}
                                        </select>
                                    </label>
                                    <label className="form-field">
                                        <span className="form-field__label">{TEXT.prefillCopyName}</span>
                                        <input
                                            className="form-field__control"
                                            type="text"
                                            value={copyPrefillState.name}
                                            onChange={(event) =>
                                                setCopyPrefillState((prev) => ({ ...prev, name: event.target.value }))
                                            }
                                            disabled={isCopyPrefillSaving}
                                        />
                                    </label>
                                    {copyPrefillError && <div className="alert alert--danger">{copyPrefillError}</div>}
                                    {copyPrefillFeedback && <div className="alert alert--success">{copyPrefillFeedback}</div>}
                                </div>
                                <footer className="modal-form__footer">
                                    <button
                                        type="button"
                                        className="button button--ghost"
                                        onClick={closeCopyPrefillModal}
                                        disabled={isCopyPrefillSaving}
                                    >
                                        {TEXT.cancel}
                                    </button>
                                    <button
                                        type="submit"
                                        className="button button--primary"
                                        disabled={isCopyPrefillSaving || !copyPrefillState.sourcePrefillId}
                                    >
                                        {isCopyPrefillSaving ? TEXT.prefillCopySaving : TEXT.prefillCopySubmit}
                                    </button>
                                </footer>
                            </form>
                        )}
                    </div>
                </div>
            )}

            {isBulkEditOpen && (
                <div
                    className="modal-overlay"
                    role="dialog"
                    aria-modal="true"
                    onClick={(event) => {
                        if (event.target === event.currentTarget) {
                            closeBulkEditModal();
                        }
                    }}
                >
                    <div className="modal-content" role="document">
                        <header className="stack">
                            <h2 className="card__title">{TEXT.bulkEditTitle}</h2>
                        </header>
                        <form className="modal-form" onSubmit={handleBulkEditSubmit}>
                            <div className="modal-form__body">
                                <label className="form-field">
                                    <span className="form-field__label">{TEXT.bulkFieldName}</span>
                                    <input
                                        className="form-field__control"
                                        type="text"
                                        value={bulkEditState.fieldName}
                                        onChange={(event) =>
                                            setBulkEditState((prev) => ({
                                                ...prev,
                                                fieldName: event.target.value,
                                            }))
                                        }
                                        required
                                    />
                                </label>
                                <label className="form-field">
                                    <span className="form-field__label">{TEXT.bulkSearchField}</span>
                                    <select
                                        className="form-field__control"
                                        value={bulkEditState.searchField}
                                        onChange={(event) =>
                                            setBulkEditState((prev) => ({
                                                ...prev,
                                                searchField: event.target.value,
                                            }))
                                        }
                                    >
                                        <option value="label">{TEXT.bulkSearchLabel}</option>
                                        <option value="key">{TEXT.bulkSearchId}</option>
                                    </select>
                                </label>
                                <label className="form-field">
                                    <span className="form-field__label">{TEXT.bulkMatchType}</span>
                                    <select
                                        className="form-field__control"
                                        value={bulkEditState.matchType}
                                        onChange={(event) =>
                                            setBulkEditState((prev) => ({
                                                ...prev,
                                                matchType: event.target.value,
                                            }))
                                        }
                                    >
                                        {MATCH_OPTIONS.map((option) => (
                                            <option key={option.value} value={option.value}>
                                                {option.label}
                                            </option>
                                        ))}
                                    </select>
                                </label>
                                <label className="form-field">
                                    <span className="form-field__label">{TEXT.bulkFieldType}</span>
                                    <select
                                        className="form-field__control"
                                        value={bulkEditState.fieldType}
                                        onChange={(event) =>
                                            setBulkEditState((prev) => ({
                                                ...prev,
                                                fieldType: event.target.value,
                                            }))
                                        }
                                    >
                                        {FIELD_TYPE_OPTIONS.map((option) => (
                                            <option key={option.value} value={option.value}>
                                                {option.label}
                                            </option>
                                        ))}
                                    </select>
                                </label>
                                <div className="flex flex--wrap">
                                    <button
                                        type="button"
                                        className="button button--light"
                                        onClick={handleBulkEditCheck}
                                        disabled={isBulkChecking || isBulkSaving}
                                    >
                                        {isBulkChecking ? TEXT.bulkChecking : TEXT.bulkCheck}
                                    </button>
                                </div>
                                {bulkMatchCount !== null && (
                                    <div className="alert alert--info">
                                        {TEXT.bulkCheckResult}: {bulkMatchCount}
                                    </div>
                                )}

                                <div className="stack">
                                    <label className="check-toggle">
                                        <input
                                            type="checkbox"
                                            checked={bulkEditState.updateRequired}
                                            onChange={(event) =>
                                                setBulkEditState((prev) => ({
                                                    ...prev,
                                                    updateRequired: event.target.checked,
                                                }))
                                            }
                                        />
                                        <span>{TEXT.bulkUpdateRequired}</span>
                                    </label>
                                    <label className="check-toggle">
                                        <input
                                            type="checkbox"
                                            checked={bulkEditState.requiredValue}
                                            disabled={!bulkEditState.updateRequired}
                                            onChange={(event) =>
                                                setBulkEditState((prev) => ({
                                                    ...prev,
                                                    requiredValue: event.target.checked,
                                                }))
                                            }
                                        />
                                        <span>{TEXT.bulkRequiredValue}</span>
                                    </label>
                                </div>

                                <div className="stack">
                                    <label className="check-toggle">
                                        <input
                                            type="checkbox"
                                            checked={bulkEditState.updateAutoFill}
                                            onChange={(event) =>
                                                setBulkEditState((prev) => ({
                                                    ...prev,
                                                    updateAutoFill: event.target.checked,
                                                }))
                                            }
                                        />
                                        <span>{TEXT.bulkUpdateAutoFill}</span>
                                    </label>
                                    <label className="form-field">
                                        <span className="form-field__label">{TEXT.bulkAutoFillValue}</span>
                                        <select
                                            className="form-field__control"
                                            value={bulkEditState.autoFill}
                                            disabled={!bulkEditState.updateAutoFill}
                                            onChange={(event) =>
                                                setBulkEditState((prev) => ({
                                                    ...prev,
                                                    autoFill: event.target.value,
                                                }))
                                            }
                                        >
                                            {AUTO_FILL_OPTIONS.map((option) => (
                                                <option key={option.value} value={option.value}>
                                                    {option.label}
                                                </option>
                                            ))}
                                        </select>
                                    </label>
                                </div>

                                <div className="stack">
                                    <label className="check-toggle">
                                        <input
                                            type="checkbox"
                                            checked={bulkEditState.updateSuggestedValues}
                                            onChange={(event) =>
                                                setBulkEditState((prev) => ({
                                                    ...prev,
                                                    updateSuggestedValues: event.target.checked,
                                                }))
                                            }
                                        />
                                        <span>{TEXT.bulkUpdateSuggested}</span>
                                    </label>
                                    <label className="form-field">
                                        <span className="form-field__label">{TEXT.bulkSuggestedValues}</span>
                                        <textarea
                                            className="form-field__control"
                                            rows={4}
                                            value={bulkEditState.suggestedValues}
                                            disabled={!bulkEditState.updateSuggestedValues}
                                            onChange={(event) =>
                                                setBulkEditState((prev) => ({
                                                    ...prev,
                                                    suggestedValues: event.target.value,
                                                }))
                                            }
                                        />
                                    </label>
                                </div>

                                <div className="stack">
                                    <label className="check-toggle">
                                        <input
                                            type="checkbox"
                                            checked={bulkEditState.updatePlaceholder}
                                            onChange={(event) =>
                                                setBulkEditState((prev) => ({
                                                    ...prev,
                                                    updatePlaceholder: event.target.checked,
                                                }))
                                            }
                                        />
                                        <span>{TEXT.bulkUpdatePlaceholder}</span>
                                    </label>
                                    <label className="form-field">
                                        <span className="form-field__label">{TEXT.bulkPlaceholder}</span>
                                        <input
                                            className="form-field__control"
                                            type="text"
                                            value={bulkEditState.placeholder}
                                            disabled={!bulkEditState.updatePlaceholder}
                                            onChange={(event) =>
                                                setBulkEditState((prev) => ({
                                                    ...prev,
                                                    placeholder: event.target.value,
                                                }))
                                            }
                                        />
                                    </label>
                                </div>
                            </div>
                            {bulkEditError && <div className="alert alert--danger">{bulkEditError}</div>}
                            {bulkEditFeedback && <div className="alert alert--success">{bulkEditFeedback}</div>}
                            <footer className="modal-form__footer">
                                <button
                                    type="button"
                                    className="button button--ghost"
                                    onClick={closeBulkEditModal}
                                    disabled={isBulkSaving || isBulkChecking}
                                >
                                    {TEXT.cancel}
                                </button>
                                <button
                                    type="submit"
                                    className="button button--primary"
                                    disabled={isBulkSaving || isBulkChecking}
                                >
                                    {isBulkSaving ? TEXT.bulkSaving : TEXT.bulkSave}
                                </button>
                            </footer>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
}
